import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const OrgContext = createContext();

export const useOrg = () => {
  const context = useContext(OrgContext);
  if (!context) {
    throw new Error('useOrg must be used within OrgProvider');
  }
  return context;
};

export const OrgProvider = ({ children }) => {
  const [organization, setOrganization] = useState(null);
  const [entities, setEntities] = useState([]);
  const [selectedEntity, setSelectedEntity] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchOrg = useCallback(async () => {
    try {
      const [orgRes, entRes] = await Promise.all([
        axios.get(`${API_URL}/api/org`),
        axios.get(`${API_URL}/api/org/entities`)
      ]);
      setOrganization(orgRes.data);
      setEntities(entRes.data?.entities || []);
      // Default to corporate if org exists and no entity selected
      if (orgRes.data && !selectedEntity) {
        setSelectedEntity({
          id: 'corporate',
          name: orgRes.data.corporate_name,
          type: 'corporate'
        });
      }
    } catch (err) {
      console.error('Failed to load organization:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchOrg();
  }, [fetchOrg]);

  const saveOrg = async (data) => {
    try {
      const res = await axios.post(`${API_URL}/api/org`, data);
      setOrganization(res.data);
      // Refresh entities
      const entRes = await axios.get(`${API_URL}/api/org/entities`);
      setEntities(entRes.data?.entities || []);
      if (!selectedEntity && res.data) {
        setSelectedEntity({
          id: 'corporate',
          name: res.data.corporate_name,
          type: 'corporate'
        });
      }
      return res.data;
    } catch (err) {
      console.error('Failed to save organization:', err);
      throw err;
    }
  };

  const selectEntity = (entity) => {
    setSelectedEntity(entity);
  };

  return (
    <OrgContext.Provider value={{
      organization,
      entities,
      selectedEntity,
      selectEntity,
      saveOrg,
      refreshOrg: fetchOrg,
      loading
    }}>
      {children}
    </OrgContext.Provider>
  );
};
